import { API_PATHS } from "../../config";
import { normalizeApiListResponse } from "../../utils/common/helpers";
import { apiRequest } from "./axios";

/**
 * RFI (Request for Information) API functions
 * Functions for managing RFI tickets
 */

// ===== Core CRUD Functions =====

// Get all RFIs
export async function getRFIs() {
  const url = "/rfis/";
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Slim RFI rows for Projects Home / Project Overview.
 *
 * @param {Object} [options]
 * @param {number|string} [options.jobId] Optional job PK to scope the list.
 * @returns {Promise<Array<Object>>}
 */
export async function getRFIProjectSummaries({ jobId } = {}) {
  const params = new URLSearchParams();
  if (jobId != null && jobId !== "") {
    params.set("job", String(jobId));
  }
  const query = params.toString();
  const url = `${API_PATHS.RFI.PROJECT_SUMMARIES}${query ? `?${query}` : ""}`;
  const data = await apiRequest(url, null, { method: "GET" });
  return normalizeApiListResponse(data);
}

// Get single RFI by ID
export async function getRFIById(id) {
  const url = `/rfis/${id}/`;
  return await apiRequest(url, null, { method: "GET" });
}

// Create new RFI
export async function createRFI(data) {
  const url = "/rfis/";
  return await apiRequest(url, data, { method: "POST" });
}

// Update existing RFI
export async function updateRFI(id, data) {
  const url = `/rfis/${id}/`;
  return await apiRequest(url, data, { method: "PATCH" });
}

// Delete RFI
export async function deleteRFI(id) {
  const url = `/rfis/${id}/`;
  return await apiRequest(url, null, { method: "DELETE" });
}

// ===== Status Actions (from TicketViewSetMixin) =====

// Toggle locked status for RFI
export async function toggleRFILocked(id) {
  const url = `/rfis/${id}/toggle-locked/`;
  return await apiRequest(url, {}, { method: "POST" });
}

// ===== RFI-Specific Functions =====

// Get next available RFI ID for a job (job number can be any string: "12345", "668-05", "0", etc.)
export async function getNextRFIId(jobNumber) {
  const url = `/rfis/next-id/?job_number=${encodeURIComponent(String(jobNumber))}`;
  return await apiRequest(url, null, { method: "GET" });
}

// Add response to RFI (internal user response mechanism)
export async function addRFIResponse(id, response) {
  const url = `/rfis/${id}/add-response/`;
  return await apiRequest(url, { response }, { method: "POST" });
}

// Update RFI status
export async function updateRFIStatus(id, status, attachmentFilenames = [], sendEmail = false) {
  const url = `/rfis/${id}/update-status/`;
  const body = { status };
  if (attachmentFilenames && attachmentFilenames.length > 0) {
    body.attachment_filenames = attachmentFilenames;
  }
  if (sendEmail) {
    body.send_email = true;
  }
  return await apiRequest(url, body, { method: "POST" });
}

// Answer RFI directly and close it (for PM direct answers)
export async function answerAndCloseRFI(id, response) {
  const url = `/rfis/${id}/answer-and-close/`;
  return await apiRequest(url, { response }, { method: "POST" });
}

// Forward RFI to external party (GC/Owner)
export async function forwardRFI(id, notes = null) {
  const url = `/rfis/${id}/forward/`;
  const body = notes ? { notes } : {};
  return await apiRequest(url, body, { method: "POST" });
}

// Get attachment folder path for RFI
export async function getRFIAttachmentFolderPath(rfiId) {
  const url = `/rfis/${rfiId}/attachment-folder-path/`;
  return await apiRequest(url, null, { method: "GET" });
}

// Send RFI to owner
export async function sendRFIToOwner(id, data) {
  const url = `/rfis/${id}/send-to-owner/`;
  return await apiRequest(url, data, { method: "POST" });
}

// Add chat message to RFI
export async function addRFIChatMessage(id, message) {
  const url = `/rfis/${id}/add-chat-message/`;
  return await apiRequest(url, { message }, { method: "POST" });
}